import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Check, X as XIcon } from "lucide-react";

/**
 * Popup OAuth callback — establishes the session cookie via /auth/session,
 * postMessages the resulting user to the opener window, then closes itself.
 * If the browser refuses to close a script-opened window, we fall back to
 * an explicit "you can close this window" screen.
 */
export default function PopupCallback() {
  const processed = React.useRef(false);
  const [state, setState] = useState("processing"); // "processing" | "success" | "error" | "manual-close"

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const notifyOpener = (payload) => {
      // 1) postMessage back to the opener (may fail if opener was severed by
      //    a cross-origin redirect during the OAuth handshake)
      try {
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(
            { source: "bliencer-auth", ...payload },
            window.location.origin,
          );
        }
      } catch {}
      // 2) BroadcastChannel — works between same-origin windows even without
      //    an opener reference. Any listening window on this origin gets it.
      try {
        const bc = new BroadcastChannel("bliencer-auth");
        bc.postMessage({ source: "bliencer-auth", ...payload });
        setTimeout(() => bc.close(), 500);
      } catch {}
      // 3) localStorage ping — universal cross-window fallback that fires a
      //    'storage' event in every same-origin tab.
      try {
        localStorage.setItem(
          "bliencer_auth_event",
          JSON.stringify({ ...payload, at: Date.now() }),
        );
      } catch {}
    };

    const tryClose = () => {
      // Try to close; if the browser prevents it, fall through to manual UI.
      try { window.close(); } catch {}
      // If we're still around after a short beat, show the "close manually" screen.
      setTimeout(() => {
        if (!window.closed) setState((s) => (s === "success" ? "manual-close" : s));
      }, 600);
    };

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setState("error");
      notifyOpener({ ok: false, error: "missing_session_id" });
      return;
    }
    const session_id = decodeURIComponent(match[1]);
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id });
        notifyOpener({ ok: true, user: data.user });
        setState("success");
        // Give the opener a beat to consume the message and refresh, then
        // attempt to auto-close.
        setTimeout(tryClose, 350);
      } catch {
        notifyOpener({ ok: false, error: "session_exchange_failed" });
        setState("error");
      }
    })();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-black text-white px-6">
      <div className="max-w-md w-full text-center">
        {state === "processing" && (
          <>
            <motion.div
              className="mx-auto h-14 w-14 rounded-full border-2 border-[#2b2b2b] border-t-white"
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 0.9, ease: "linear" }}
            />
            <div className="mt-6 text-lg font-semibold tracking-tight">
              Completing sign-in…
            </div>
            <div className="mt-2 text-sm text-[#a0a0a0]">
              Just a second while we set up your session.
            </div>
          </>
        )}

        {(state === "success" || state === "manual-close") && (
          <>
            <motion.div
              className="mx-auto h-16 w-16 rounded-full flex items-center justify-center"
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 320, damping: 22 }}
              style={{
                background: "linear-gradient(180deg, rgba(34,197,94,0.18) 0%, rgba(34,197,94,0.06) 100%)",
                border: "1.5px solid rgba(34,197,94,0.6)",
                boxShadow: "0 0 0 6px rgba(34,197,94,0.10)",
              }}
            >
              <Check size={26} style={{ color: "#22c55e" }} />
            </motion.div>
            <div className="mt-6 text-2xl font-bold tracking-tighter">You're signed in</div>
            {state === "success" ? (
              <div className="mt-2 text-sm text-[#a0a0a0]">
                Closing this window and taking you back to your order…
              </div>
            ) : (
              <>
                <div className="mt-2 text-sm text-[#a0a0a0]">
                  Your order is ready — you can close this window and return to the checkout tab.
                </div>
                <button
                  onClick={() => { try { window.close(); } catch {} }}
                  className="mt-6 squircle-sm border border-[#2b2b2b] px-5 h-10 text-sm inline-flex items-center gap-2 hover:bg-[#111] btn-anim"
                  data-testid="popup-close-btn"
                >
                  <XIcon size={14} /> Close this window
                </button>
              </>
            )}
          </>
        )}

        {state === "error" && (
          <>
            <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center"
              style={{ background: "rgba(239,68,68,0.15)", border: "1.5px solid rgba(239,68,68,0.6)" }}>
              <XIcon size={26} style={{ color: "#ef4444" }} />
            </div>
            <div className="mt-6 text-lg font-semibold">Sign-in failed</div>
            <div className="mt-2 text-sm text-[#a0a0a0]">
              Please close this window and try again from the checkout page.
            </div>
            <button
              onClick={() => { try { window.close(); } catch {} }}
              className="mt-6 squircle-sm border border-[#2b2b2b] px-5 h-10 text-sm inline-flex items-center gap-2 hover:bg-[#111] btn-anim"
            >
              <XIcon size={14} /> Close
            </button>
          </>
        )}
      </div>
    </div>
  );
}
